import type { Params } from 'react-router';
import type { ProductDetailsFragment } from '../saleor-graphql';
import type { Breadcrumb } from '../types';

export interface BreadcrumbStrategy {
  id: string;
  priority: number;
  canApply: (product: ProductDetailsFragment) => boolean;
  generate: (params: {
    product: ProductDetailsFragment;
    routeParams: Params;
    urls: any;
  }) => Breadcrumb[];
}

export interface BreadcrumbConfig {
  homeName?: string;
  includeHome?: boolean;
  includeShop?: boolean;
  shopName?: string;
  includeProductName?: boolean;
}

interface AttributeBreadcrumbOptions {
  id: string;
  attributeSlug: string;
  getName: (value: string) => string;
  getUrl: (params: {
    routeParams: Params;
    product: ProductDetailsFragment;
    attributeValue: string;
  }) => string;
  priority?: number;
  condition?: (product: ProductDetailsFragment) => boolean;
}

interface CategoryBreadcrumbOptions {
  id: string;
  getName?: (category: ProductDetailsFragment['category']) => string;
  getUrl: (params: {
    routeParams: Params;
    category: ProductDetailsFragment['category'];
  }) => string;
  priority?: number;
  skipIfMatchesAttribute?: string;
}

interface CustomBreadcrumbOptions {
  id: string;
  priority?: number;
  condition: (product: ProductDetailsFragment) => boolean;
  generate: (params: {
    product: ProductDetailsFragment;
    routeParams: Params;
    urls: any;
  }) => Breadcrumb[];
}

export function createAttributeBreadcrumbStrategy(
  options: AttributeBreadcrumbOptions
): BreadcrumbStrategy {
  return {
    id: options.id,
    priority: options.priority ?? 10,
    canApply: (product) => {
      if (options.condition && !options.condition(product)) {
        return false;
      }
      const attribute = product.attributes.find(
        (attr) => attr.attribute.slug === options.attributeSlug
      );
      return !!attribute?.values?.length;
    },
    generate: ({ product, routeParams, urls }) => {
      const attribute = product.attributes.find(
        (attr) => attr.attribute.slug === options.attributeSlug
      );
      
      if (!attribute?.values?.length) {
        return [];
      }

      const value = attribute.values[0];
      return [{
        name: options.getName(value.name || value.slug),
        url: options.getUrl({
          routeParams,
          product,
          attributeValue: value.slug,
        }),
      }];
    },
  };
}

export function createCategoryBreadcrumbStrategy(
  options: CategoryBreadcrumbOptions
): BreadcrumbStrategy {
  return {
    id: options.id,
    priority: options.priority ?? 20,
    canApply: (product) => {
      if (!product.category) {
        return false;
      }
      
      // Skip if category matches a specified attribute value
      if (options.skipIfMatchesAttribute) {
        const attribute = product.attributes.find(
          (attr) => attr.attribute.slug === options.skipIfMatchesAttribute
        );
        if (attribute?.values?.[0]?.name === product.category.name) {
          return false;
        }
      }
      
      return true;
    },
    generate: ({ product, routeParams }) => {
      if (!product.category) {
        return [];
      }

      return [{
        name: options.getName 
          ? options.getName(product.category)
          : product.category.name,
        url: options.getUrl({
          routeParams,
          category: product.category,
        }),
      }];
    },
  };
}

export function createCustomBreadcrumbStrategy(
  options: CustomBreadcrumbOptions
): BreadcrumbStrategy {
  return {
    id: options.id,
    priority: options.priority ?? 5,
    canApply: options.condition,
    generate: options.generate,
  };
}

export function createProductBreadcrumbBuilder(
  strategies: BreadcrumbStrategy[],
  config: BreadcrumbConfig = {}
) {
  const {
    homeName = 'Home',
    includeHome = true,
    includeShop = false,
    shopName = 'Shop',
    includeProductName = true,
  } = config;

  return (params: {
    product: ProductDetailsFragment;
    routeParams: Params;
    urls: any;
  }): Breadcrumb[] => {
    const { product, routeParams, urls } = params;
    const breadcrumbs: Breadcrumb[] = [];

    // Add home breadcrumb
    if (includeHome) {
      breadcrumbs.push({
        name: homeName,
        url: urls.home(routeParams),
      });
    }

    // Add shop breadcrumb if configured
    if (includeShop) {
      breadcrumbs.push({
        name: shopName,
        url: urls.products?.index ? urls.products.index(routeParams) : urls.home(routeParams),
      });
    }

    // Sort strategies by priority (higher priority first)
    const sortedStrategies = [...strategies].sort((a, b) => b.priority - a.priority);

    // Apply strategies
    for (const strategy of sortedStrategies) {
      if (strategy.canApply(product)) {
        const crumbs = strategy.generate({ product, routeParams, urls });
        breadcrumbs.push(...crumbs);
      }
    }

    // Add product name as last breadcrumb
    if (includeProductName) {
      breadcrumbs.push({
        name: product.name,
        url: urls.products?.details 
          ? urls.products.details(routeParams, { slug: product.slug })
          : '#',
      });
    }

    return breadcrumbs;
  };
}

// Helper function to prevent duplicate consecutive breadcrumbs
export function deduplicateConsecutiveBreadcrumbs(breadcrumbs: Breadcrumb[]): Breadcrumb[] {
  return breadcrumbs.filter((crumb, index) => {
    if (index === 0) return true;
    const prevCrumb = breadcrumbs[index - 1];
    return crumb.name !== prevCrumb.name || crumb.url !== prevCrumb.url;
  });
}